class MiuraHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.miura-hero-slider__slide'));
    this.dots = Array.from(this.querySelectorAll('.miura-hero-slider__dot'));
    this.prev = this.querySelector('.miura-hero-slider__arrow--prev');
    this.next = this.querySelector('.miura-hero-slider__arrow--next');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval || 6000);
    this.autoplay = this.dataset.autoplay === 'true';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.timer = null;
    this.videoEndedHandler = null;
    this.pausedByHover = false;

    if (!this.slides.length) return;

    this.show(this.index, false);

    this.prev?.addEventListener('click', () => this.show(this.index - 1));
    this.next?.addEventListener('click', () => this.show(this.index + 1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.show(Number(dot.dataset.slideTo || 0)));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.pauseCurrentSlide());
      this.addEventListener('mouseleave', () => this.resumeCurrentSlide());
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseCurrentSlide(true);
      else this.resumeCurrentSlide(true);
    });

    this.scheduleNextForCurrentSlide();
  }

  disconnectedCallback() {
    this.clearTimer();
    this.removeVideoEndedHandler();
  }

  clearTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }

  removeVideoEndedHandler() {
    if (!this.videoEndedHandler) return;
    const video = this.getCurrentVideo();
    if (video) video.removeEventListener('ended', this.videoEndedHandler);
    this.videoEndedHandler = null;
  }

  getCurrentSlide() {
    return this.slides[this.index];
  }

  getCurrentVideo() {
    return this.getCurrentSlide()?.querySelector('video') || null;
  }

  scheduleNextForCurrentSlide() {
    this.clearTimer();
    this.removeVideoEndedHandler();

    if (!this.autoplay || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const video = this.getCurrentVideo();

    if (video) {
      video.loop = false;
      this.videoEndedHandler = () => this.show(this.index + 1);
      video.addEventListener('ended', this.videoEndedHandler, { once: true });

      if (video.ended) {
        this.show(this.index + 1);
        return;
      }

      video.play().catch(() => {
        /* If autoplay is blocked for any reason, avoid freezing the slider forever. */
        this.timer = window.setTimeout(() => this.show(this.index + 1), this.interval);
      });

      return;
    }

    this.timer = window.setTimeout(() => this.show(this.index + 1), this.interval);
  }

  pauseCurrentSlide(fromVisibilityChange = false) {
    this.pausedByHover = !fromVisibilityChange;
    this.clearTimer();

    const video = this.getCurrentVideo();
    if (video && !video.paused) video.pause();
  }

  resumeCurrentSlide(fromVisibilityChange = false) {
    if (fromVisibilityChange && document.hidden) return;
    if (!fromVisibilityChange && this.pauseOnHover && this.matches(':hover')) return;

    const video = this.getCurrentVideo();
    if (video && !video.ended) {
      video.play().catch(() => {});
    }

    this.scheduleNextForCurrentSlide();
  }

  show(nextIndex, restart = true) {
    const total = this.slides.length;
    const normalizedIndex = ((nextIndex % total) + total) % total;

    this.clearTimer();
    this.removeVideoEndedHandler();

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === normalizedIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');

      const video = slide.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.playsInline = true;
      video.loop = false;

      if (active) {
        try { video.currentTime = 0; } catch (error) {}
        video.play().catch(() => {});
      } else {
        video.pause();
        try { video.currentTime = 0; } catch (error) {}
      }
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === normalizedIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.index = normalizedIndex;
    if (restart) this.scheduleNextForCurrentSlide();
  }
}

if (!customElements.get('miura-hero-slider')) {
  customElements.define('miura-hero-slider', MiuraHeroSlider);
}
